import { useCallback, useState } from 'react';

/**
 * 通用表单状态管理 Hook
 */
export interface FormState<T = Record<string, any>> {
  values: T;
  errors: Record<string, string>;
  isLoading: boolean;
}

export interface UseFormStateOptions<T> {
  initialValues: T;
  onSubmit?: (values: T) => Promise<void> | void;
  validate?: (values: T) => Record<string, string>;
}

export const useFormState = <T extends Record<string, any>>({
  initialValues,
  onSubmit,
  validate,
}: UseFormStateOptions<T>) => {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  // 更新单个字段值
  const setValue = useCallback(
    (field: keyof T, value: any) => {
      setValues((prev) => ({ ...prev, [field]: value }));
      // 清除该字段的错误
      if (errors[field as string]) {
        setErrors((prev) => ({ ...prev, [field as string]: '' }));
      }
    },
    [errors],
  );

  // 批量更新值
  const setMultipleValues = useCallback((newValues: Partial<T>) => {
    setValues((prev) => ({ ...prev, ...newValues }));
  }, []);

  // 设置错误
  const setError = useCallback((field: string, error: string) => {
    setErrors((prev) => ({ ...prev, [field]: error }));
  }, []);

  // 清除错误
  const clearError = useCallback((field: string) => {
    setErrors((prev) => ({ ...prev, [field]: '' }));
  }, []);

  // 清除所有错误
  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  // 设置加载状态
  const setLoading = useCallback((loading: boolean) => {
    setIsLoading(loading);
  }, []);

  // 提交表单
  const handleSubmit = useCallback(async () => {
    if (!onSubmit) return;

    // 验证表单
    if (validate) {
      const validationErrors = validate(values);
      setErrors(validationErrors);

      if (Object.keys(validationErrors).length > 0) {
        return;
      }
    }

    try {
      setIsLoading(true);
      await onSubmit(values);
    } catch (error: any) {
      setError('general', error.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [values, validate, onSubmit, setError]);

  // 重置表单
  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setIsLoading(false);
  }, [initialValues]);

  const resetErrors = useCallback(() => {
    setErrors({});
  }, []);

  return {
    values,
    errors,
    isLoading,
    setValue,
    setValues: setMultipleValues,
    setError,
    clearError,
    clearErrors,
    setLoading,
    handleSubmit,
    reset,
    resetErrors,
  };
};
